import { Snackbar } from '@mui/material';
import * as React from 'react';
import { IJob } from 'types/job';
import {
  Alert,
  SvgConfirmIllustration,
} from '@insights-ltd/design-library/components';
import { useSaveStrings } from 'api/hooks';
import ConfirmationDialog from 'components/Feedback/ConfirmationDialog';
import { useJobContext } from 'pages/Job/hooks/jobContext';

interface DialogProps {
  open: boolean;
  job: IJob;
  onClose: () => void;
}

type Stage = 'confirm' | 'loading' | 'success';

const RemoveStringsDialogs = ({ open, job, onClose }: DialogProps) => {
  const {
    stringSelection: { selectedStrings, stopRemovingStrings },
  } = useJobContext();
  const [stage, setStage] = React.useState<Stage>('confirm');
  React.useEffect(() => {
    setStage('confirm');
  }, [open]);
  const {
    mutate: saveStrings,
    status: stringsSaveStatus,
    reset,
  } = useSaveStrings();
  const [numberStringsRemoved, setNumberStringsRemoved] =
    React.useState<number>(0);

  const onSaveSelection = () => {
    setStage('loading');
    return saveStrings(
      {
        jobId: job.id,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        keys: job
          .translations!.map((translation) => translation.key)
          .filter((key) => !selectedStrings.includes(key)),
      },
      {
        onSuccess: () => {
          setNumberStringsRemoved(selectedStrings.length);
          setStage('success');
          stopRemovingStrings();
        },
      },
    );
  };

  return (
    <>
      <ConfirmationDialog
        dataTestId="remove"
        title="Remove strings"
        text={`Are you sure you want to remove the ${selectedStrings.length} selected string(s) from job ${job.id}?`}
        isOpen={open && stage === 'confirm'}
        onClose={onClose}
        primaryButtonText="Remove strings"
        primaryButtonColour="error"
        onSubmit={() => onSaveSelection()}
        secondaryButtonText="Cancel"
        onSecondarySubmit={onClose}
      />

      <ConfirmationDialog
        dataTestId="removed"
        title="Strings removed"
        svg={<SvgConfirmIllustration />}
        text={`You have successfully removed ${numberStringsRemoved} string(s) from job ${job.id}`}
        isOpen={open && stage === 'success'}
        onClose={onClose}
        primaryButtonText="Close"
        onSubmit={onClose}
      />

      <Snackbar
        open={stringsSaveStatus === 'error'}
        autoHideDuration={6000}
        onClose={reset}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert elevation={6} variant="filled" onClose={reset} severity="error">
          Failed to remove strings. Please try again later.
        </Alert>
      </Snackbar>
    </>
  );
};

export default RemoveStringsDialogs;
